import { ChevronRight } from 'lucide-react';
import React from 'react';
import {
    Button as AriaButton,
    Disclosure as AriaDisclosure,
    type DisclosureProps as AriaDisclosureProps,
    DisclosurePanel,
    Heading,
    composeRenderProps,
} from 'react-aria-components';

import { classnames } from '@/utils/classnames.ts';

export interface DisclosureProps extends Omit<AriaDisclosureProps, 'children'> {
    title: React.ReactNode;
    // Shown next to the title while closed, so the key values stay visible.
    summary?: React.ReactNode;
    // Below the title, open or closed: one line while closed, in full while open, so it stays where it is.
    description?: React.ReactNode;
    // Shown at the end of the title's row while open.
    action?: React.ReactNode;
    // Shown at the end of the title's row while closed, e.g. what's inside.
    closedAction?: React.ReactNode;
    // `sm` for a part of a form (e.g. its advanced fields), `md` for a section of a page.
    size?: 'sm' | 'md';
    // Without any, opening only shows the description in full and the action.
    children?: React.ReactNode;
}

// The chevron in a disclosure's trigger, turned down while it's open. Keyed to the trigger's own aria-expanded, so a
// disclosure inside another's panel turns its own only.
export const DisclosureChevron = ({ className }: { className?: string }) => (
    <ChevronRight className={classnames('shrink-0 transition-transform in-aria-expanded:rotate-90', className)} />
);

// A section that opens and closes under its title, e.g. settings most people never need. Its content stays mounted
// while closed, so form fields in it keep their values.
const DisclosureRoot = ({
    title,
    summary,
    description,
    action,
    closedAction,
    size = 'sm',
    children,
    className,
    ...props
}: DisclosureProps) => (
    <AriaDisclosure {...props} className={composeRenderProps(className, (className) => classnames('group', className))}>
        <div className={classnames('flex items-center justify-between gap-3', size === 'md' && 'min-h-9')}>
            <Heading className={classnames('flex min-w-0 font-medium', size === 'sm' && 'text-sm')}>
                <AriaButton
                    slot="trigger"
                    className={classnames(
                        '-ml-1.5 flex min-w-0 items-center gap-1.5 rounded-lg py-1 pr-2 pl-1.5 outline-none data-[focus-visible]:ring-2 data-[focus-visible]:ring-primary data-[hovered]:bg-surface-muted',
                        size === 'sm' && 'text-text-muted data-[hovered]:text-text'
                    )}
                >
                    <DisclosureChevron className={size === 'sm' ? 'size-4' : 'text-text-muted'} />
                    <span className="shrink-0">{title}</span>
                    {summary && (
                        <span className="min-w-0 truncate font-normal text-text-muted group-data-[expanded]:hidden">
                            · {summary}
                        </span>
                    )}
                </AriaButton>
            </Heading>
            {action && <div className="hidden group-data-[expanded]:block">{action}</div>}
            {closedAction && <div className="group-data-[expanded]:hidden">{closedAction}</div>}
        </div>
        {/* Lined up with the title, past the chevron (size-4) and its gap. */}
        {description && (
            <p
                className={classnames(
                    'truncate pl-5.5 leading-relaxed text-text-muted group-data-[expanded]:whitespace-normal',
                    size === 'sm' && 'text-sm'
                )}
            >
                {description}
            </p>
        )}
        {children && (
            <DisclosurePanel>
                <div className="flex flex-col gap-4 pt-3">{children}</div>
            </DisclosurePanel>
        )}
    </AriaDisclosure>
);

export default Object.assign(DisclosureRoot, {
    Chevron: DisclosureChevron,
});
